import React from 'react';
import { TextLink, Typography } from '@guildeducationinc/recess';
import { GetMemberProgramQuery } from 'src/graphqlTypes/catalogServiceTypes';
import { ProgramContentItems } from '@src/components/ProgramDetails/ProgramContentItems';
import {
	WALMART_CREDIT_REQUIREMENT,
	WALMART_APPLICATION_REQ_SUBTITLE,
	LIVE_BETTER_U_URL,
	WALMART_APPROVAL_LETTER_REQUIREMENT,
	WALMART_GPA_REQUIREMENT,
} from '@src/constants/walmartCustomRequirementCopy';
import { RequirementSet } from '../RequirementSet';
import styles from '../EducationRequirements.module.scss';

interface Props {
	contentItems?: GetMemberProgramQuery['memberProgram']['contentItems'];
	programId: string;
	requirements?: GetMemberProgramQuery['memberProgram']['requirements'];
}

interface RequirementsCopy {
	body?: string | React.ReactNode;
}

interface RequirementsBlockProps {
	body?: string | React.ReactNode;
	additionalRequirements?: RequirementsCopy[];
}

const courseworkRequirements: RequirementsCopy[] = [{ body: WALMART_CREDIT_REQUIREMENT }];
const approvalLetterRequirement = [{ body: WALMART_APPROVAL_LETTER_REQUIREMENT }];
export const walmartRelaunchPrograms = ['442', '719', '865', '867', '3313'];

const CollegePreparation: React.FC = () => {
	return (
		<Typography variant='body2'>
			Complete 12 or more credits through a college prep program. A full list of available programs
			can be found on{' '}
			<TextLink href={LIVE_BETTER_U_URL} size='large' variant='standalone'>
				www.livebetteru.com
			</TextLink>
		</Typography>
	);
};

const GPARequirements: React.FC = () => {
	return <Typography variant='body2'>{WALMART_GPA_REQUIREMENT}</Typography>;
};

const ApprovalLetterRequirements: React.FC = () => {
	return (
		<ProgramContentItems
			ariaLabel='credit hour validation requirements'
			className='margin--top_24'
			contentItems={approvalLetterRequirement}
			sectionTitle='Live Better U Credit Hour Validation email'
		/>
	);
};

// custom Walmart requirements, PATH-743 / PATH-2168
const WalmartRequirementsBlock: React.FC<RequirementsBlockProps> = ({ additionalRequirements }) => {
	const collegeRequirements: RequirementsCopy[] = [{ body: <CollegePreparation /> }];
	const currentCourseworkRequirements = [...courseworkRequirements];

	if (additionalRequirements) {
		currentCourseworkRequirements.push(...additionalRequirements);
	}

	return (
		<>
			<Typography className={styles.subtitle} variant='body2'>
				{WALMART_APPLICATION_REQ_SUBTITLE}
			</Typography>
			<RequirementSet contentItems={collegeRequirements} level='College preparation' />
			<Typography className={styles.or} variant='label2'>
				Or
			</Typography>
			<RequirementSet contentItems={currentCourseworkRequirements} level='Prior coursework' />
		</>
	);
};

export const WalmartEducationRequirements: React.FC<Props> = ({
	contentItems,
	programId,
	requirements,
}) => {
	const isRelaunchProgram = walmartRelaunchPrograms.includes(programId);

	const gpaRequirements: RequirementsCopy[] = [{ body: <GPARequirements /> }];

	// for program 3313 we need to ignore normal contentItems for the program, so that we can use a custom combination with the Walmart college prep copy
	if (programId === '3313') {
		const { minCollegeCredits } = requirements;
		const hsRequirementSet = [];
		const collegeRequirementSet = [];

		contentItems = [{ title: 'Students must have a high school diploma or equivalent.' }];

		hsRequirementSet.push({ body: <CollegePreparation /> });

		if (minCollegeCredits) {
			const collegeCredits = {
				body: `Complete ${minCollegeCredits} or more college credits at a regionally accredited school.`,
			};
			collegeRequirementSet.push(collegeCredits);
		}

		collegeRequirementSet.push({ body: <GPARequirements /> });

		return (
			<ProgramContentItems
				ariaLabel='education requirements'
				contentItems={contentItems}
				sectionTitle='Education'
			>
				<Typography
					className={styles.subtitle}
					data-testid='conditional-edu-requirements'
					variant='body2'
				>
					{WALMART_APPLICATION_REQ_SUBTITLE}
				</Typography>
				<RequirementSet contentItems={hsRequirementSet} level='College preparation' />
				<Typography className={styles.or} variant='label2'>
					Or
				</Typography>
				<RequirementSet contentItems={collegeRequirementSet} level='Prior coursework' />
				<ApprovalLetterRequirements />
			</ProgramContentItems>
		);
	}

	// for all other BA programs, display existing education requirements + walmart requirements
	return (
		<>
			<ProgramContentItems
				ariaLabel='education requirements'
				contentItems={contentItems}
				sectionTitle='Education'
			>
				<WalmartRequirementsBlock
					additionalRequirements={isRelaunchProgram ? gpaRequirements : undefined}
				/>
			</ProgramContentItems>

			{isRelaunchProgram && <ApprovalLetterRequirements />}
		</>
	);
};
